import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { tasksAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';

/**
 * DailyUpdateForm
 * Props: taskId (string), onSubmitted (callback)
 * Owns: fetch/display of all updates + today's form
 */
export default function DailyUpdateForm({ taskId, onSubmitted }) {
  const { user } = useRole();

  const [updates,       setUpdates]       = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(true);
  const [submitting,    setSubmitting]    = useState(false);

  // Form state
  const [statusNote,    setStatusNote]    = useState('');
  const [blocker,       setBlocker]       = useState('');
  const [progress,      setProgress]      = useState(0);
  const [todayEntryId,  setTodayEntryId]  = useState(null);

  // Today's date string YYYY-MM-DD (local)
  const todayStr = new Date().toLocaleDateString('en-CA'); // en-CA gives YYYY-MM-DD

  const fetchUpdates = useCallback(async () => {
    setLoadingUpdates(true);
    try {
      const res  = await tasksAPI.getUpdates(taskId);
      const list = res.data?.data?.updates || [];
      setUpdates(list);

      // Find today's entry for the current user
      const todayEntry = list.find(u => {
        const entryDate = u.update_date
          ? new Date(u.update_date).toLocaleDateString('en-CA')
          : '';
        const isToday = entryDate === todayStr;
        const isMine  = String(u.submitted_by?._id || u.submitted_by) === String(user?._id);
        return isToday && isMine;
      });

      if (todayEntry) {
        setTodayEntryId(todayEntry._id);
        setStatusNote(todayEntry.status_note || '');
        setBlocker(todayEntry.blocker || '');
        setProgress(todayEntry.progress_percentage ?? 0);
      } else {
        setTodayEntryId(null);
      }
    } catch {
      // silently fail
    } finally {
      setLoadingUpdates(false);
    }
  }, [taskId, todayStr, user?._id]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUpdates(); }, [fetchUpdates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!statusNote.trim()) {
      toast.error('Status note is required');
      return;
    }
    setSubmitting(true);
    try {
      await tasksAPI.addUpdate(taskId, {
        status_note:         statusNote.trim(),
        blocker:             blocker.trim() || undefined,
        progress_percentage: progress,
        update_date:         todayStr,
      });
      toast.success('Update saved');
      await fetchUpdates();
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save update');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b',
    outline: 'none', boxSizing: 'border-box', resize: 'vertical',
    fontFamily: 'inherit',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: '#64748b',
    display: 'block', marginBottom: 4,
  };

  return (
    <div>
      {/* ── Today's Update Card ───────────────────────────────────────────── */}
      <div style={{
        background: '#fff', borderRadius: 10,
        border: '1.5px solid #e2e8f0', padding: '18px 20px',
        marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <span style={{ fontSize: 18 }}>📝</span>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1B2A6B' }}>
            {todayEntryId ? "Today's Update" : "Add Today's Update"}
          </span>
          <span style={{
            marginLeft: 'auto', fontSize: 11, color: '#94a3b8',
            background: '#f8fafc', borderRadius: 4, padding: '2px 7px',
          }}>
            {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Status Note */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Status Note <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <textarea
              style={{ ...inputStyle, minHeight: 72 }}
              placeholder="What did you work on today? Any progress updates?"
              value={statusNote}
              onChange={e => setStatusNote(e.target.value)}
              required
            />
          </div>

          {/* Blocker */}
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Blocker{' '}
              <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>(optional)</span>
            </label>
            <textarea
              style={{ ...inputStyle, minHeight: 52 }}
              placeholder="Any blockers or impediments?"
              value={blocker}
              onChange={e => setBlocker(e.target.value)}
            />
          </div>

          {/* Progress slider */}
          <div style={{ marginBottom: 18 }}>
            <label style={labelStyle}>
              Progress:{' '}
              <span style={{ color: '#1B2A6B', fontWeight: 700 }}>{progress}%</span>
            </label>
            <input
              type="range"
              min={0} max={100} step={5}
              value={progress}
              onChange={e => setProgress(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#1B2A6B', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
              <span>0%</span><span>50%</span><span>100%</span>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '9px 22px', borderRadius: 8, border: 'none',
              background: submitting ? '#94a3b8' : '#1B2A6B',
              color: '#fff', fontWeight: 700, fontSize: 13,
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Saving…' : todayEntryId ? "Update Today's Entry" : 'Save Update'}
          </button>
        </form>
      </div>

      {/* ── Past Updates List ─────────────────────────────────────────────── */}
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>
          Past Updates{!loadingUpdates && ` (${updates.length})`}
        </div>

        {loadingUpdates ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Loading updates…</div>
        ) : updates.length === 0 ? (
          <div style={{
            color: '#94a3b8', fontSize: 13, background: '#f8fafc',
            borderRadius: 8, padding: '28px 16px', textAlign: 'center',
          }}>
            No updates yet. Add your first daily update above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {updates.map(u => {
              const entryDate = u.update_date ? new Date(u.update_date) : null;
              const isToday   = entryDate && entryDate.toLocaleDateString('en-CA') === todayStr;
              return (
                <div
                  key={u._id}
                  style={{
                    background: '#f8fafc', borderRadius: 8,
                    padding: '12px 14px', position: 'relative',
                    border: `1px solid ${isToday ? '#bfdbfe' : '#f1f5f9'}`,
                  }}
                >
                  {isToday && (
                    <span style={{
                      position: 'absolute', top: 10, right: 12,
                      fontSize: 10, background: '#dbeafe', color: '#2563eb',
                      borderRadius: 4, padding: '1px 7px', fontWeight: 600,
                    }}>Today</span>
                  )}

                  {/* Header row: avatar + date + name + progress */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: '#1B2A6B', color: '#C9A84C',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 10, flexShrink: 0,
                    }}>
                      {(u.submitted_by?.name?.[0] || '?').toUpperCase()}
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1B2A6B' }}>
                      {entryDate
                        ? entryDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                        : '—'}
                    </span>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>{u.submitted_by?.name || '—'}</span>
                    {u.progress_percentage !== undefined && (
                      <span style={{
                        marginLeft: 'auto',
                        background: '#dbeafe', color: '#2563eb',
                        borderRadius: 4, padding: '1px 7px',
                        fontSize: 11, fontWeight: 600, flexShrink: 0,
                        marginRight: isToday ? 60 : 0,
                      }}>
                        {u.progress_percentage}%
                      </span>
                    )}
                  </div>

                  {/* Status note */}
                  <div style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.5, marginBottom: u.blocker ? 6 : 0 }}>
                    {u.status_note}
                  </div>

                  {/* Blocker */}
                  {u.blocker && (
                    <div style={{
                      fontSize: 12, color: '#dc2626',
                      background: '#fee2e2', borderRadius: 4,
                      padding: '4px 8px', marginTop: 4,
                    }}>
                      🚫 Blocker: {u.blocker}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
